import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { initFirebaseTickets } from './ticketSystemApi';

const STORAGE_KEYS = {
  initialUserId: 'userUniqueId',
  authProvider: 'authProvider',
  googleEmail: 'googleEmail',
  googleDisplayName: 'googleDisplayName',
  facebookEmail: 'facebookEmail',
  facebookDisplayName: 'facebookDisplayName',
  lineEmail: 'lineEmail',
  lineDisplayName: 'lineDisplayName'
};

const IDENTITY_LINKS_COLLECTION = 'auth_identity_links';

export const normalizeEmail = (email) => (email || '').trim().toLowerCase();

export const getStoredInitialUserId = () => {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEYS.initialUserId);
  return raw && raw.trim() ? raw.trim() : null;
};

export const setStoredInitialUserId = (userId) => {
  if (typeof localStorage === 'undefined') return;
  if (!userId || !String(userId).trim()) return;
  localStorage.setItem(STORAGE_KEYS.initialUserId, String(userId).trim());
};

export const ensureInitialUserId = (generateFn) => {
  const current = getStoredInitialUserId();
  if (current) return current;

  const created = generateFn();
  setStoredInitialUserId(created);
  return created;
};

const normalizeProvider = (provider) => String(provider || '').trim().toLowerCase();

const getProviderStorageKeys = (provider) => {
  const normalizedProvider = normalizeProvider(provider);
  if (normalizedProvider === 'facebook') {
    return {
      emailKey: STORAGE_KEYS.facebookEmail,
      displayNameKey: STORAGE_KEYS.facebookDisplayName
    };
  }
  if (normalizedProvider === 'line') {
    return {
      emailKey: STORAGE_KEYS.lineEmail,
      displayNameKey: STORAGE_KEYS.lineDisplayName
    };
  }

  return {
    emailKey: STORAGE_KEYS.googleEmail,
    displayNameKey: STORAGE_KEYS.googleDisplayName
  };
};

export const setProviderSession = ({ provider, email, displayName }) => {
  if (typeof localStorage === 'undefined') return;

  const normalizedProvider = normalizeProvider(provider) || 'google';
  const { emailKey, displayNameKey } = getProviderStorageKeys(normalizedProvider);

  localStorage.setItem(STORAGE_KEYS.authProvider, normalizedProvider);

  const normalizedEmail = normalizeEmail(email);
  if (normalizedEmail) {
    localStorage.setItem(emailKey, normalizedEmail);
  }

  if (displayName) {
    localStorage.setItem(displayNameKey, displayName);
  }
};

export const clearProviderSession = (provider) => {
  if (typeof localStorage === 'undefined') return;

  const normalizedProvider = normalizeProvider(provider);
  if (!normalizedProvider || normalizedProvider === 'google') {
    localStorage.removeItem(STORAGE_KEYS.googleEmail);
    localStorage.removeItem(STORAGE_KEYS.googleDisplayName);
  }

  if (!normalizedProvider || normalizedProvider === 'facebook') {
    localStorage.removeItem(STORAGE_KEYS.facebookEmail);
    localStorage.removeItem(STORAGE_KEYS.facebookDisplayName);
  }

  if (!normalizedProvider || normalizedProvider === 'line') {
    localStorage.removeItem(STORAGE_KEYS.lineEmail);
    localStorage.removeItem(STORAGE_KEYS.lineDisplayName);
  }

  localStorage.removeItem(STORAGE_KEYS.authProvider);
};

export const clearStoredIdentity = () => {
  if (typeof localStorage === 'undefined') return;

  localStorage.removeItem(STORAGE_KEYS.initialUserId);
  clearProviderSession();
};

export const setGoogleSession = ({ googleEmail, displayName }) => {
  setProviderSession({
    provider: 'google',
    email: googleEmail,
    displayName
  });
};

export const clearGoogleSession = () => {
  clearProviderSession('google');
};

const buildProviderLinkDocId = (provider, providerEmail) => {
  const normalizedProvider = normalizeProvider(provider) || 'google';
  return `${normalizedProvider}:${normalizeEmail(providerEmail)}`;
};

export const resolveCanonicalUserIdByProvider = async ({
  provider,
  providerEmail,
  firebaseUid,
  fallbackInitialUserId,
  displayName,
  saveUserProfile,
  profileOverrides
}) => {
  const normalizedProvider = normalizeProvider(provider) || 'google';
  const normalizedEmail = normalizeEmail(providerEmail);

  if (!normalizedEmail) {
    return {
      canonicalUserId: fallbackInitialUserId,
      isExistingLink: false
    };
  }

  const { db } = initFirebaseTickets();
  const linkRef = doc(db, IDENTITY_LINKS_COLLECTION, buildProviderLinkDocId(normalizedProvider, normalizedEmail));
  let existingLink = null;
  try {
    const linkSnap = await getDoc(linkRef);
    existingLink = linkSnap.exists() ? linkSnap.data() : null;
  } catch (error) {
    const code = String(error?.code || '');
    if (code.includes('permission-denied') || code.includes('unauthenticated')) {
      existingLink = null;
    } else {
      throw error;
    }
  }

  const canonicalUserId = existingLink?.canonicalUserId || fallbackInitialUserId;

  await setDoc(linkRef, {
    provider: normalizedProvider,
    providerEmail: normalizedEmail,
    firebaseUid: firebaseUid || null,
    canonicalUserId,
    displayName: displayName || null,
    linkedAt: existingLink?.linkedAt || serverTimestamp(),
    lastLoginAt: serverTimestamp()
  }, { merge: true });

  const profilePayload = {
    uid: canonicalUserId,
    displayName: displayName || existingLink?.displayName || `${normalizedProvider} User`,
    email: normalizedEmail,
    firebaseUid: firebaseUid || null,
    role: 'user',  // デフォルトロール
    // authProvider, googleEmail, googleDisplayName は auth_identity_links にあるので不要
    ...(profileOverrides || {})
  };

  await saveUserProfile(profilePayload);

  return {
    canonicalUserId,
    isExistingLink: Boolean(existingLink)
  };
};

export const resolveCanonicalUserIdByGoogle = async ({
  googleEmail,
  firebaseUid,
  fallbackInitialUserId,
  displayName,
  saveUserProfile
}) => {
  return resolveCanonicalUserIdByProvider({
    provider: 'google',
    providerEmail: googleEmail,
    firebaseUid,
    fallbackInitialUserId,
    displayName,
    saveUserProfile
  });
};